import React, { Suspense } from 'react';
// import PropTypes from 'prop-types';

import {
  Loading,
  NotFoundPage,
  UnavailablePage,
} from 'murder-hornet-ui';
import { createBrowserHistory as createHistory } from 'history';
import { Route, Routes } from 'react-router-dom';

import {
  AppWrapper,
  CustomRouter,
  lazyWithRetry,
  PrivateRoute,
  PublicRoute,
  ScrollToTop,
} from './helpers';

import { useToggle } from '../hooks';

// LANDING
const HomePage = lazyWithRetry(() => import('../components/HomePage'));

// AUTH
const LogIn = lazyWithRetry(() => import('../components/auth/LogIn'));
const Register = lazyWithRetry(() => import('../components/auth/Register'));
const ValidateMFA = lazyWithRetry(() => import('../components/auth/ValidateMFA'));
const ForgotPassword = lazyWithRetry(() => import('../components/auth/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('../components/auth/ResetPassword'));

// DASHBOARD
const Dashboard = lazyWithRetry(() => import('../components/Dashboard'));

// APPLICATION PIPELINE
const AllApplications = lazyWithRetry(() => import('../components/application-pipeline/AllApplications'));
const StartNewApplication = lazyWithRetry(() => import('../components/application-pipeline/StartNewApplication'));
const ViewApplication = lazyWithRetry(() => import('../components/application-pipeline/ViewApplication'));

// PROGRAMS PIPELINE
const ProgramsPipeline = lazyWithRetry(() => import('../components/programs-pipeline/ProgramsPipeline'));
const ViewProgramInPipeline = lazyWithRetry(() => import('../components/programs-pipeline/ViewProgram'));
const ProgramsDetailsInPipeline = lazyWithRetry(() => import('../components/programs-pipeline/tabs/Details'));
const ProgramBundleInPipeline = lazyWithRetry(() => import('../components/programs-pipeline/tabs/ProgramBundle'));
const ProgramInterestRatesInPipeline = lazyWithRetry(() => import('../components/programs-pipeline/tabs/InterestRates'));
const ProgramFeeScheduleInPipeline = lazyWithRetry(() => import('../components/programs-pipeline/tabs/FeeSchedule'));

// FUNDING SOURCES PIPELINE - FS
const FundingSourcesPipeline = lazyWithRetry(() => import('../components/funding-sources-pipeline/FundingSourcesPipeline'));
const ViewFundingSourceInPipeline = lazyWithRetry(() => import('../components/funding-sources-pipeline/ViewFundingSource'));
const FundingSourceDetailsInPipeline = lazyWithRetry(() => import('../components/funding-sources-pipeline/tabs/Details'));
const FundingSourceInterestRatesInPipeline = lazyWithRetry(() => import('../components/funding-sources-pipeline/tabs/InterestRates'));
const FundingSourceProgramsInPipeline = lazyWithRetry(() => import('../components/funding-sources-pipeline/tabs/Programs'));

// REPORTS
// const KPICharts = lazyWithRetry(() => import('../components/reports/KPICharts'));
// const AllReports = lazyWithRetry(() => import('../components/reports/AllReports'));

// MY SETTINGS
const IndexMySettings = lazyWithRetry(() => import('../components/settings/my-settings/IndexMySettings'));
const PersonalInformation = lazyWithRetry(() => import('../components/settings/my-settings/PersonalInformation'));
const ResetPasswordLoggedIn = lazyWithRetry(() => import('../components/settings/my-settings/ResetPassword'));
const Sessions = lazyWithRetry(() => import('../components/settings/my-settings/Sessions'));

// SYSTEM SETTINGS
const SystemSettings = lazyWithRetry(() => import('../components/settings/system-settings/SystemSettings'));
const LenderDetails = lazyWithRetry(() => import('../components/settings/system-settings/LenderDetails'));
const PaymentManagement = lazyWithRetry(() => import('../components/settings/system-settings/PaymentManagement'));
const AllLocations = lazyWithRetry(() => import('../components/settings/system-settings/AllLocations'));
const AllUsers = lazyWithRetry(() => import('../components/settings/system-settings/AllUsers'));
const ViewUser = lazyWithRetry(() => import('../components/settings/system-settings/ViewUser'));
const InviteUser = lazyWithRetry(() => import('../components/settings/system-settings/InviteUser'));
const InviteUserBulk = lazyWithRetry(() => import('../components/settings/system-settings/InviteUserBulk'));
const RequestInviteUser = lazyWithRetry(() => import('../components/settings/system-settings/RequestInviteUser'));
const AllPermissionGroups = lazyWithRetry(() => import('../components/settings/system-settings/AllPermissionGroups'));
const MessageCenterTemplates = lazyWithRetry(() => import('../components/settings/system-settings/MessageCenterTemplates'));
const ActivityLogTemplates = lazyWithRetry(() => import('../components/settings/system-settings/ActivityLogTemplates'));

// HELP
const Help = lazyWithRetry(() => import('../components/help/Help'));

// RELEASE NOTES
const ReleaseNotes = lazyWithRetry(() => import('../components/settings/help/release-notes/ReleaseNotes'));
const ViewReleaseNote = lazyWithRetry(() => import('../components/settings/help/release-notes/ViewReleaseNote'));

// HELP TICKETS
// const HelpTickets = lazyWithRetry(() => import('../components/help/help-tickets/HelpTickets'));
// const CreateHelpTicket = lazyWithRetry(() => import('../components/help/help-tickets/CreateHelpTicket'));
// const ViewHelpTicket = lazyWithRetry(() => import('../components/help/help-tickets/ViewHelpTicket'));
// const HelpTicketDetails = lazyWithRetry(() => import('../components/help/help-tickets/tabs/HelpTicketDetails'));
// const HelpTicketCommunications = lazyWithRetry(() => import('../components/help/help-tickets/tabs/HelpTicketCommunications'));


// TEST
const TestPage = lazyWithRetry(() => import('../components/TestPage'));

export const history = createHistory();


const AppRouter = () => {
  const [sideNavOpen, toggleSideNav] = useToggle(true);

  return <CustomRouter history={history}>
    <ScrollToTop>
      <div className='container-scroller'>

        <Suspense fallback={<Loading main />}>
          <Routes>
            <Route element={<AppWrapper sideNavOpen={sideNavOpen} toggleSideNav={toggleSideNav} />}>
              {/* LANDING */}
              <Route path='/' element={<PublicRoute><HomePage /></PublicRoute>} />

              {/* AUTH */}
              <Route path='/login/:organization_uuid' element={<PublicRoute><LogIn /></PublicRoute>} />
              <Route path='/register/:organization_uuid/:token_uuid' element={<PublicRoute><Register /></PublicRoute>} />
              <Route path='/validate/session/:organization_uuid' element={<PublicRoute><ValidateMFA /></PublicRoute>} />
              <Route path='/forgot-password/:organization_uuid' element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path='/reset-password/:organization_uuid/:token_uuid' element={<PublicRoute><ResetPassword /></PublicRoute>} />

              {/* DASHBOARD */}
              <Route path='/dashboard' element={<PrivateRoute><Dashboard /></PrivateRoute>} />

              {/* APPLICATIONS */}
              <Route path='/applications' element={<PrivateRoute perform={[
                'lender:application:read-all',
                'lender:application:read-location',
                'lender:application:read-assigned',
              ]}><AllApplications /></PrivateRoute>} />
              <Route path='/applications/start' element={<PrivateRoute perform={['lender:application:write']}><StartNewApplication /></PrivateRoute>} />
              <Route path='/applications/:application_uuid/:layout?' element={<PrivateRoute perform={[
                'lender:application:read-all',
                'lender:application:read-location',
                'lender:application:read-assigned',
              ]}><ViewApplication /></PrivateRoute>} />

              {/* PROGRAMS PIPELINE*/}
              <Route path='/programs-pipeline' element={<PrivateRoute perform={['lender:fs-pipeline:read-all']}><ProgramsPipeline /></PrivateRoute>} />
              <Route path='/programs-pipeline/:program_uuid' element={<PrivateRoute perform={['lender:fs-pipeline:read-all']}><ViewProgramInPipeline /></PrivateRoute>}>
                <Route path='details' element={<ProgramsDetailsInPipeline />} />
                <Route path='program-bundle' element={<ProgramBundleInPipeline />} />
                <Route path='interest-rates' element={<ProgramInterestRatesInPipeline />} />
                <Route path='fee-templates' element={<ProgramFeeScheduleInPipeline />} />
              </Route>

              {/* FUNDING SOURCES PIPELINE*/}
              <Route path='/funding-sources-pipeline' element={<PrivateRoute perform={['lender:fs-pipeline:read-all']}><FundingSourcesPipeline /></PrivateRoute>} />
              <Route path='/funding-sources-pipeline/:funding_source_uuid' element={<PrivateRoute perform={['lender:fs-pipeline:read-all']}><ViewFundingSourceInPipeline /></PrivateRoute>} >
                <Route path='details' element={<FundingSourceDetailsInPipeline />} />
                <Route path='interest-rates' element={<FundingSourceInterestRatesInPipeline />} />
                <Route path='programs' element={<FundingSourceProgramsInPipeline />} />
              </Route>

              {/* REPORTS */}
              {/* <Route path='/reports' element={<PrivateRoute perform={['lender:report:read-all-reports']}><AllReports /></PrivateRoute>} />
              <Route path='/charts' element={<PrivateRoute perform={['lender:report:read-kpi-charts']}><KPICharts /></PrivateRoute>} /> */}

              {/* MY SETTINGS */}
              <Route path='/my-settings'>
                <Route index element={<PrivateRoute><IndexMySettings /></PrivateRoute>} />
                <Route path='personal-information' element={<PrivateRoute><PersonalInformation /></PrivateRoute>} />
                <Route path='reset-password' element={<PrivateRoute><ResetPasswordLoggedIn /></PrivateRoute>} />
                <Route path='sessions' element={<PrivateRoute><Sessions /></PrivateRoute>} />
              </Route>

              {/* SYSTEM SETTINGS */}
              <Route path='/system-settings'>
                <Route index element={<PrivateRoute perform={[
                  'lender:lender-settings:read',
                  'lender:payment-settings:read',
                  'lender:location-settings:read',
                  'lender:user-settings:read',
                  'lender:communication-templates-settings:read',
                  'lender:activity-log-note-templates-settings:read',
                ]}><SystemSettings /></PrivateRoute>} />
                <Route path='lender-details' element={<PrivateRoute perform={['lender:lender-settings:read']}><LenderDetails /></PrivateRoute>} />
                <Route path='payment-management' element={<PrivateRoute perform={['lender:payment-settings:read']}><PaymentManagement /></PrivateRoute>} />
                <Route path='locations' element={<PrivateRoute perform={['lender:location-settings:read']}><AllLocations /></PrivateRoute>} />
                <Route path='users' element={<PrivateRoute perform={['lender:user-settings:read']}><AllUsers /></PrivateRoute>} />
                <Route path='users/invite' element={<PrivateRoute perform={['lender:user-settings:write']}><InviteUser /></PrivateRoute>} />
                <Route path='users/invite-bulk' element={<PrivateRoute perform={['lender:user-settings:write']}><InviteUserBulk /></PrivateRoute>} />
                <Route path='users/invite-request' element={<PrivateRoute perform={['lender:user-settings:read']}><RequestInviteUser /></PrivateRoute>} />
                <Route path='users/:user_uuid' element={<PrivateRoute perform={['lender:user-settings:read']}><ViewUser /></PrivateRoute>} />
                <Route path='permissions' element={<PrivateRoute perform={['lender:user-settings:write']}><AllPermissionGroups /></PrivateRoute>} />
                <Route path='message-center-templates' element={<PrivateRoute perform={['lender:communication-templates-settings:read']}><MessageCenterTemplates /></PrivateRoute>} />
                <Route path='activity-log-templates' element={<PrivateRoute perform={['lender:activity-log-note-templates-settings:read']}><ActivityLogTemplates /></PrivateRoute>} />
              </Route>

              {/* HELP */}
              <Route path='help'>
                <Route index element={<Help />} />

                {/* RELEASE NOTES */}
                <Route path='release-notes' element={<PrivateRoute perform={['lender:release-notes:read']}><ReleaseNotes /></PrivateRoute>} />
                <Route path='release-notes/:release_note_uuid' element={<PrivateRoute perform={['lender:release-notes:read']}><ViewReleaseNote /></PrivateRoute>} />

                {/* HELP TICKETS */}
                {/* <Route path='help-tickets' element={<PrivateRoute><HelpTickets /></PrivateRoute>} />
                <Route path='help-tickets/new' element={<PrivateRoute perform='lender:special:test'><CreateHelpTicket /></PrivateRoute>} />
                <Route path='help-tickets/:help_ticket_uuid' element={<PrivateRoute><ViewHelpTicket /></PrivateRoute>}>
                  <Route path='details' element={<HelpTicketDetails />} />
                  <Route path='communications' element={<HelpTicketCommunications />} />
                </Route> */}
              </Route>;

              {/* TEST */}
              {environment !== 'prod' && <Route path='/test' element={<TestPage />} />}

              {/* 503 Error Redirect */}
              <Route path='/unavailable' element={<UnavailablePage />} />

              <Route path='*' element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Suspense>

        {/* <Footer
            config={{ footer: config.footer, faqPage: config.faqPage, reportFraudPage: config.reportFraudPage }}
            version={VERSION}
            environment={environment}
            googleTranslate={<GoogleTranslate />} /> */}
        {environment !== 'prod' && <div className='position-absolute float-end end-0'>Version {VERSION}</div>}
      </div>
    </ScrollToTop>
  </CustomRouter>;
};


AppRouter.propTypes = {
};


export default AppRouter;
